import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, Play, Users, Calendar, Mic, Building2, Trophy, GraduationCap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import OTPLoginModal from './OTPLoginModal';
import api from '../services/api';

const HERO_WORDS = ['Game', 'Session', 'Match'];

import { FACILITY_IMAGES, SPORT_LABELS, DEFAULT_VENUE_IMAGE } from '../config/venueConfig';
import { HorizontalScroller } from './HorizontalScroller';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useCustomerAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    api.getPublicFacilities().then(setFacilities).catch(() => {});
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex(prev => (prev + 1) % HERO_WORDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      {showLogin && <OTPLoginModal onClose={() => setShowLogin(false)} onSuccess={() => setShowLogin(false)} />}

      {/* HERO */}
      <section className="relative h-[85vh] min-h-[540px] max-h-[720px] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-[#0A2C93]">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1400&q=80"
            alt=""
            className="w-full h-full object-cover opacity-30 mix-blend-luminosity"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2C93] via-[#0A2C93]/40 to-transparent" />

        <div className="relative h-full flex flex-col items-center justify-center px-4 text-center">
          {/* Logo */}
          <div className="mb-8">
            <span className="font-logo text-3xl sm:text-4xl font-black text-white tracking-tight">
              PlaySpaces
            </span>
            <div className="h-0.5 w-24 mx-auto mt-1 bg-white/40" />
          </div>

          {/* Headline */}
          <h1 className="mb-6">
            <span className="block font-heading font-bold text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Book your
            </span>
            <span className="block h-[52px] sm:h-[64px] relative overflow-hidden my-1">
              <AnimatePresence mode="wait">
                <motion.span
                  key={HERO_WORDS[wordIndex]}
                  initial={{ y: 52, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -52, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex items-center justify-center font-heading font-bold text-5xl sm:text-6xl text-[#00D650] tracking-tight"
                >
                  {HERO_WORDS[wordIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
            <span className="block font-heading font-bold text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              instantly
            </span>
            <span className="sr-only"> — book sports venues in Bengaluru</span>
          </h1>

          <p className="font-body text-base sm:text-lg text-white/70 mb-8 max-w-md">
            Premium sports venues in Bengaluru. Book in seconds.
          </p>

          {/* Search bar */}
          <div className="w-full max-w-md" data-testid="hero-search">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search games, venues, experiences"
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white text-slate-900 placeholder-slate-400 font-body text-sm focus:outline-none focus:ring-2 focus:ring-[#00D650] shadow-xl"
                onFocus={() => navigate('/explore')}
                readOnly
              />
            </div>
          </div>

          {/* Login prompt */}
          {!isAuthenticated && (
            <button
              onClick={() => setShowLogin(true)}
              className="mt-6 text-sm text-white/60 hover:text-white transition-colors font-body"
              data-testid="hero-login-prompt"
            >
              Already part of our community? <span className="underline font-semibold text-white/80">Log in</span>
            </button>
          )}
        </div>
      </section>

      {/* BRAND STATEMENT */}
      <section className="py-12 px-4 text-center bg-white" data-testid="brand-statement">
        <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#0A2C93] mb-3 tracking-tight">
          More than just a place to play
        </h2>
        <p className="font-body text-sm sm:text-base text-[#737373] max-w-md mx-auto">
          A complete ecosystem around sport, fitness, and community
        </p>
      </section>

      {/* ECOSYSTEM GRID */}
      <section className="px-4 pb-12 max-w-5xl mx-auto" data-testid="ecosystem-grid">
        <div className="grid grid-cols-2 gap-[10px]">
          <EcosystemCard
            title="Book a Venue"
            subtitle="Find & book near you"
            bgColor="#1a1a2e"
            iconBg="rgba(0,214,80,0.15)"
            Icon={Building2}
            iconColor="#00D650"
            borderColor="rgba(0,214,80,0.4)"
            onClick={() => navigate('/book')}
            testId="ecosystem-book-venue"
          />
          <EcosystemCard
            title="Join a Game"
            subtitle="Community games near you"
            bgColor="#0A2C93"
            iconBg="rgba(0,214,80,0.15)"
            Icon={Users}
            iconColor="#00D650"
            borderColor="rgba(0,214,80,0.4)"
            onClick={() => navigate('/join-a-game')}
            testId="ecosystem-join-game"
          />
          <EcosystemCard
            title="Events & Tournaments"
            subtitle="Register for upcoming events"
            bgColor="#0A2C93"
            iconBg="rgba(245,158,11,0.15)"
            Icon={Trophy}
            iconColor="#F59E0B"
            borderColor="rgba(245,158,11,0.4)"
            onClick={() => navigate('/events')}
            testId="ecosystem-events"
          />
          <EcosystemCard
            title="Get Coached"
            subtitle="Expert coaching sessions"
            bgColor="#1a1a2e"
            iconBg="rgba(0,214,80,0.15)"
            Icon={GraduationCap}
            iconColor="#00D650"
            borderColor="rgba(0,214,80,0.4)"
            onClick={() => navigate('/coaching')}
            testId="ecosystem-coaching"
          />
        </div>
      </section>

      {/* STORY FLOW */}
      <section className="py-12 px-4 bg-[#F8FAFC]" data-testid="story-flow">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#0A2C93] text-center mb-8">
            Your Perfect Game Day
          </h2>
          <div className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide snap-x sm:justify-center">
            {[
              { step: '1', title: 'Book Slot', desc: 'Choose your venue, sport & time', icon: Calendar },
              { step: '2', title: 'Play', desc: 'Show up and enjoy your game', icon: Play },
              { step: '3', title: 'Highlights', desc: 'Get your game highlights', icon: Mic },
              { step: '4', title: 'Recovery', desc: 'Cool down & refuel at the cafe', icon: Users },
            ].map(item => (
              <div key={item.step} className="flex-shrink-0 w-[160px] snap-start text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#0A2C93] flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-xs font-heading font-semibold text-[#00D650] mb-1">Step {item.step}</div>
                <div className="font-heading font-bold text-sm text-[#0A2C93]">{item.title}</div>
                <div className="font-body text-xs text-[#737373] mt-1">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-12 px-4 bg-white" data-testid="social-proof">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center gap-8 sm:gap-16">
            <div>
              <div className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2C93]">5+</div>
              <div className="font-body text-xs text-[#737373] mt-1">Premium Venues</div>
            </div>
            <div>
              <div className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2C93]">3</div>
              <div className="font-body text-xs text-[#737373] mt-1">Sports</div>
            </div>
            <div>
              <div className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2C93]">1000+</div>
              <div className="font-body text-xs text-[#737373] mt-1">Players Monthly</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-10 px-4 bg-[#F8FAFC]" data-testid="cta-section">
        <div className="max-w-md mx-auto flex flex-col gap-3">
          <button
            onClick={() => navigate('/book')}
            className="w-full py-4 rounded-full bg-[#0A2C93] hover:bg-[#082273] text-white font-heading font-semibold text-base transition-colors"
            data-testid="cta-book-venue"
          >
            Book a Venue
          </button>
          <button
            onClick={() => navigate('/explore')}
            className="w-full py-4 rounded-full bg-[#00D650] hover:bg-[#00C248] text-[#0A2C93] font-heading font-semibold text-base transition-colors"
            data-testid="cta-join-game"
          >
            Join a Game
          </button>
          <button
            className="w-full py-4 rounded-full border-2 border-[#0A2C93]/20 text-[#0A2C93]/50 font-heading font-semibold text-base cursor-not-allowed"
            disabled
            data-testid="cta-explore-exp"
          >
            Explore Experiences — Coming Soon
          </button>
        </div>
      </section>

      {/* VENUE PREVIEW */}
      <section className="py-12 px-4 bg-white" data-testid="venue-preview">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#0A2C93]">Our Venues</h2>
            <button
              onClick={() => navigate('/explore')}
              className="flex items-center gap-1 text-sm font-heading font-semibold text-[#0A2C93] hover:text-[#082273] transition-colors"
              data-testid="view-all-venues"
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <HorizontalScroller className="gap-4 pb-2" testId="home-venues-scroller">
            {facilities.map(f => (
              <VenuePreviewCard key={f.id} facility={f} onClick={() => navigate(`/venue/${f.slug || f.id}`)} />
            ))}
          </HorizontalScroller>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A2C93] text-white py-10 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <span className="font-logo text-2xl font-black">PlaySpaces</span>
          <div className="h-px w-16 mx-auto mt-2 mb-4 bg-white/20" />
          <button
            onClick={() => navigate('/about')}
            data-testid="footer-about-link"
            className="font-body text-sm text-white/70 hover:text-white transition-colors mb-4"
          >
            About Us
          </button>
          <p className="font-body text-sm text-white/50">
            &copy; 2026 PlaySpaces. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

/* ---------- Sub-components ---------- */

const EcosystemCard = ({ title, subtitle, bgColor, iconBg, Icon, iconColor, borderColor, pillBg, opacity, onClick, comingSoon, testId }) => (
  <button
    onClick={comingSoon ? undefined : onClick}
    className="ecosystem-card relative rounded-[14px] overflow-hidden text-left transition-transform duration-200 hover:-translate-y-0.5 w-full min-h-[150px] sm:min-h-[160px]"
    style={{
      padding: '12px',
      backgroundColor: bgColor,
      border: `0.5px solid ${borderColor || 'transparent'}`,
      cursor: comingSoon ? 'not-allowed' : 'pointer',
      opacity: opacity != null ? opacity : 1,
    }}
    data-testid={testId}
  >
    {/* Top row: icon + optional pill */}
    <div className="flex items-start justify-between">
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: '38px',
          height: '38px',
          backgroundColor: iconBg || 'rgba(255,255,255,0.15)',
          borderRadius: '9px',
        }}
      >
        <Icon size={20} color={iconColor} strokeWidth={2} />
      </div>
      {comingSoon && (
        <div
          className="flex-shrink-0"
          style={{
            padding: '3px 9px',
            backgroundColor: pillBg || 'rgba(255,255,255,0.18)',
            borderRadius: '999px',
          }}
        >
          <span style={{ fontSize: '9px', color: 'white', fontWeight: 600, letterSpacing: '0.4px' }}>
            Coming Soon
          </span>
        </div>
      )}
    </div>

    {/* Title and subtitle pinned to bottom */}
    <div className="absolute" style={{ bottom: '12px', left: '10px', right: '10px' }}>
      <h3
        className="ecosystem-card-title font-heading font-bold text-white leading-tight text-[13px] sm:text-[15px] tracking-tight"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.05em' }}
      >
        {title}
      </h3>
      <p
        className="ecosystem-card-subtitle font-body mt-0.5 text-[10px] sm:text-[11px]"
        style={{
          color: 'rgba(255,255,255,0.7)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {subtitle}
      </p>
    </div>
  </button>
);

const VenuePreviewCard = ({ facility, onClick }) => {
  const img = FACILITY_IMAGES[facility.id] || DEFAULT_VENUE_IMAGE;
  const sports = (facility.sports || []).map(s => s === 'pickle' ? 'pickleball' : s);

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-[260px] sm:w-[300px] snap-start bg-white border border-slate-100 rounded-2xl overflow-hidden text-left hover:-translate-y-1 transition-all duration-200"
      style={{ boxShadow: '0 8px 30px rgb(0 0 0 / 0.04)' }}
      data-testid={`venue-card-${facility.id}`}
    >
      <div className="relative h-36">
        <img src={img} alt={facility.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        <div className="absolute top-2 left-2 flex gap-1">
          {sports.map(s => (
            <span key={s} className="px-2 py-0.5 rounded-full bg-white/90 backdrop-blur-sm text-[10px] font-heading font-semibold text-[#0A2C93] uppercase tracking-wide">
              {SPORT_LABELS[s] || s}
            </span>
          ))}
        </div>
      </div>
      <div className="p-3">
        <h3 className="font-heading font-bold text-sm text-[#0A2C93]">{facility.name}</h3>
        <div className="flex items-center justify-between mt-2">
          <span className="font-body text-xs text-[#737373]">{facility.location || 'Bengaluru'}</span>
          <span className="flex items-center gap-1 text-xs font-heading font-semibold text-[#00D650]">
            View <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </button>
  );
};

export default HomePage;
